import {
  BodyMedium,
  BodySmall,
  BoxWithImage,
  DeleteIcon,
  FeelingValue,
  Label,
  LabelColor,
  LabelSize,
  LinkButton,
  Tooltip,
  theme,
  DeprecatedSendIcon,
  DeprecatedAlertIcon,
  DeprecatedCommentWarningIcon,
} from '@arnold/common';
import { getLocalizedDateWithoutLeadingZeroes } from '@arnold/core';
import styled from '@emotion/styled/macro';
import React, { useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { RemoveRespondentModal } from '../../components/modals/RemoveRespondentModal';
import { SendAgainModal } from '../../components/modals/SendAgainModal';
import { UpdateRespondent } from '../../components/modals/UpdateRespondent';
import {
  Features,
  GetProcessReportRespondentsQuery,
  ReportSequenceRespondentDetailQueryQuery,
  SurveyStatus,
} from '../../generated/hooks';
import { getAlertsData, getTopicGroupTranslation } from '../../lib/reportHelper';
import useRespondentsFeelings from './useRespondentsFeelings';
import { useScrollToRespondentTable } from '../../lib/reports/reportUtils';

type ReportSequence = NonNullable<ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']>;

interface IProps {
  reportSequence: ReportSequence;
  groupedRespondents: GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'];
  accessToken: string;
  encryptedRespondentId?: string;
  urlParams: string;
  respondentLink?: boolean;
  publicAccess?: boolean;
  loading?: boolean;
  showDeleteButton?: boolean;
  onReload: (shoudReload?: boolean) => void;
  reloadRespondentsData?: () => void;
  createDetailUrl?: (topicGroupId: string) => string;
  isDetail?: boolean;
}

const StyledCard = styled(Card)`
  padding: ${theme.spacing.f};
  padding-top: ${theme.spacing.g};
`;

const StyledAlertIcon = styled(DeprecatedAlertIcon)`
  &:hover {
    path {
      fill: ${theme.colors.chart.alert[0]};
    }
  }
`;

const StyledCommentWarningIcon = styled(DeprecatedCommentWarningIcon)`
  &:hover {
    path {
      fill: ${theme.colors.chart.warning[0]};
    }
  }
`;

const GrayBodySmall = styled(BodySmall)`
  color: ${theme.colors.text.secondary};
`;

const StyledTooltip = styled(Tooltip)`
  margin: 0 ${theme.spacing.c};
  display: inline;
`;

const tooltipProps: { animation: 'fade'; multiple: boolean } = {
  animation: 'fade',
  multiple: false,
};

const StyledLinkButton = styled(LinkButton)`
  width: 100%;

  & svg path {
    fill: ${(props) => (props.disabled ? theme.colors.text.disabled : theme.colors.iconAction.primary)};
  }
`;
const ButtonText = styled.span`
  margin-left: ${theme.spacing.c};
`;

type SendAgainModalProps = {
  name: string;
  onSubmit: () => void;
};
const RespondentDetailFeelings = (props: IProps) => {
  const {
    reportSequence,
    groupedRespondents,
    accessToken,
    loading,
    showDeleteButton,
    onReload,
    reloadRespondentsData,
    isDetail,
  } = props;
  const {
    respondents,
    showModal,
    editRespondent,
    setEditRespondent,
    respondentToDelete,
    modal,
    dateFrom,
    isHiring,
    containsAnonymity,
    setModal,
  } = useRespondentsFeelings(props);
  const [sendAgainModalProps, setAgainModalProps] = useState<SendAgainModalProps | null>(null);
  const history = useHistory();
  const { t, i18n } = useTranslation('Report');
  const respondentTableRef = useRef<HTMLDivElement>(null);
  useScrollToRespondentTable(respondentTableRef);

  const respondent = respondents[0];
  if (!respondent) {
    return null;
  }

  return (
    <>
      {editRespondent && (
        <UpdateRespondent
          show
          respondent={editRespondent.respondent}
          contact={editRespondent.contact}
          organizationId={reportSequence.metadata.organization.id}
          cancel={() => {
            setEditRespondent(null);
          }}
          onComplete={() => {
            setEditRespondent(null);
            if (reloadRespondentsData) {
              reloadRespondentsData();
            } else {
              onReload(false);
            }
          }}
          accessToken={accessToken}
          defaultContryCallingCode={reportSequence.metadata.organization.config?.countryCallingCode}
        />
      )}

      {loading && <BoxWithImage className="d-flex justify-content-center" image={<LoadingAnimation />}></BoxWithImage>}
      <StyledCard ref={respondentTableRef}>
        <BodyMedium>
          <strong>
            {isHiring ? t('hiringFistSurveyAt') : t('addedOn')}:{' '}
            {isHiring
              ? respondent.firstSurveySentAt
                ? getLocalizedDateWithoutLeadingZeroes(new Date(respondent.firstSurveySentAt))
                : '-'
              : respondent.respondent.group.addedOn
                ? getLocalizedDateWithoutLeadingZeroes(new Date(respondent.respondent.group.addedOn), i18n.language)
                : t('deleted')}
          </strong>
        </BodyMedium>
        {isHiring && respondent.position && (
          <BodyMedium>
            {t('position')}: {respondent.position}
          </BodyMedium>
        )}

        <StyledHr />
        {reportSequence.surveyGroup?.steps.map((step) => {
          const r = respondent.steps?.find((s) => s.step.processStep.id === step!.processStep.id);

          const chat = getAlertsData(reportSequence, t, false, true).filter(
            (chat) =>
              chat.messages.length &&
              chat.respondentId === r?.channel?.respondentId &&
              chat.topicGroupId === step.processStep.stepTopicGroup.id,
          )[0];
          const topicGroupName = getTopicGroupTranslation(
            step!.processStep.stepTopicGroup,
            reportSequence.metadata.language,
            '',
          ).value;

          const numberOfErrors = chat?.messages?.filter((m) => m.weight > 1).length;
          const numberOfWarnings = chat?.messages?.filter((m) => m.weight === 1).length;

          const infoNodes: React.ReactNode[] = [];
          let sendNode: React.ReactNode = null;

          if (r?.metricAnswer?.value !== null && r?.metricAnswer?.value !== undefined) {
            infoNodes.push(
              <div className="d-flex align-items-center">
                <FeelingValue value={r.metricAnswer.value} data-cy="metric-question">
                  {r.metricAnswer.value}
                </FeelingValue>
                {numberOfErrors > 0 && (
                  <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
                    {' '}
                    <div className="d-flex">
                      <StyledAlertIcon /> <GrayBodySmall margin="0 0 0 6px">({numberOfErrors})</GrayBodySmall>
                    </div>
                  </StyledTooltip>
                )}
                {numberOfWarnings > 0 && (
                  <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
                    <div className="d-flex">
                      <StyledCommentWarningIcon data-cy="report-respondents-cell-warning" />{' '}
                      <GrayBodySmall margin="0 0 0 6px">({numberOfWarnings})</GrayBodySmall>
                    </div>
                  </StyledTooltip>
                )}
              </div>,
            );
          }
          if (r?.undelivered) {
            infoNodes.push(
              <div>
                <Label color={LabelColor.DANGER} size={LabelSize.XSMALL}>
                  {t('undelivered')}
                </Label>
                <LinkButton
                  data-cy="report-respondents-cell"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!r?.undelivered?.contact || !respondent) {
                      return;
                    }
                    setEditRespondent({
                      respondent: respondent.respondent.respondent,
                      contact: r?.undelivered?.contact,
                    });
                  }}
                >
                  {t('editContact')}
                </LinkButton>
              </div>,
            );
          }
          if (
            (r?.survey &&
              (([SurveyStatus.Prepared, SurveyStatus.Notstarted, SurveyStatus.Activating].includes(r?.survey.status) &&
                (respondent.respondent.respondent.deleted ||
                  respondent.respondent.group.deletedRespondents?.includes(respondent.respId))) ||
                ([SurveyStatus.Completed, SurveyStatus.Archived, SurveyStatus.Ongoing].includes(r?.survey.status) &&
                  !r?.survey.respondentIds.includes(respondent.respondent.respondent.id)))) ||
            !r?.survey
          ) {
            if (
              containsAnonymity ||
              !reportSequence.metadata.organization.featureNames?.includes(Features.SendInvitationFromReport) ||
              (r?.survey &&
                (respondent.respondent.respondent.deleted ||
                  respondent.respondent.group.deletedRespondents?.includes(respondent.respId))) ||
              !r?.survey ||
              isHiring
            ) {
              infoNodes.push(
                <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                  {t('notSent')}
                </Label>,
              );
            } else {
              infoNodes.push(
                <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                  {t('notSent')}
                </Label>,
              );
              if (r?.survey?.status !== SurveyStatus.Activating) {
                sendNode = (
                  <LinkButton
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      setAgainModalProps({
                        name: topicGroupName,
                        onSubmit: () => {
                          if (r?.survey?.status === SurveyStatus.Ongoing) {
                            r?.sendAgainSurvey();
                          } else if (r?.survey) {
                            r?.reopenSurvey();
                          } else {
                            r?.createMissingSurvey();
                          }
                        },
                      });
                    }}
                    data-cy="send-again-button"
                  >
                    <StyledTooltip
                      {...tooltipProps}
                      title={t(r?.disabled ? 'todayWasAlreadySent' : 'sendAgainTooltip')}
                    >
                      <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                    </StyledTooltip>
                  </LinkButton>
                );
              }
            }
          }

          if (r?.survey && [SurveyStatus.Prepared].includes(r?.survey.status)) {
            infoNodes.push(
              <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                {t('preparedTooltip')}
              </Label>,
            );
          }

          if (
            r?.survey?.status === SurveyStatus.Ongoing &&
            r?.survey.respondentIds.includes(respondent.respondent.respondent.id) &&
            !r?.channel?.finishedAt
          ) {
            infoNodes.push(
              <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                {t('ongoingTooltip')}
              </Label>,
            );
          }

          if (r?.survey && !containsAnonymity && [SurveyStatus.Notstarted].includes(r?.survey.status) && !isHiring) {
            infoNodes.push(
              <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                {t('notSent')}
              </Label>,
            );
            sendNode = (
              <LinkButton
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  setAgainModalProps({
                    name: topicGroupName,
                    onSubmit: () => {
                      r?.updateNotStartedSurvey();
                    },
                  });
                }}
                data-cy="send-again-button"
              >
                <StyledTooltip {...tooltipProps} title={t(r?.disabled ? 'todayWasAlreadySent' : 'sendAgainTooltip')}>
                  <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                </StyledTooltip>
              </LinkButton>
            );
          }
          if (!step.hasMetricQuestion && r?.channel?.withAnswer) {
            infoNodes.push(
              <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                {t('withAnswer')}
              </Label>,
            );
          }

          if (step.hasMetricQuestion && r?.channel?.withAnswer && infoNodes.length === 0) {
            infoNodes.push(
              <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL}>
                {t('answeredWithoutMetricAnswerTooltip')}
              </Label>,
            );
          }

          if (infoNodes.length === 0) {
            infoNodes.push(
              <>
                <Label color={LabelColor.NEUTRAL} size={LabelSize.XSMALL} subtle>
                  {t('noAnswer')}
                </Label>
                {numberOfErrors > 0 && (
                  <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
                    {' '}
                    <div className="d-flex">
                      <StyledAlertIcon /> <GrayBodySmall margin="0 0 0 6px">({numberOfErrors})</GrayBodySmall>
                    </div>
                  </StyledTooltip>
                )}
                {numberOfWarnings > 0 && (
                  <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
                    <div className="d-flex">
                      <StyledCommentWarningIcon data-cy="report-respondents-cell-warning" />{' '}
                      <GrayBodySmall margin="0 0 0 6px">({numberOfWarnings})</GrayBodySmall>
                    </div>
                  </StyledTooltip>
                )}
              </>,
            );
          }
          if (
            r?.channel &&
            !r?.channel?.withAnswer &&
            !containsAnonymity &&
            reportSequence.metadata.organization.featureNames?.includes(Features.SendInvitationFromReport) &&
            !respondent.respondent.group.deletedRespondents?.includes(respondent.respId) &&
            !respondent.respondent.respondent.deleted &&
            !isHiring
          ) {
            sendNode = (
              <LinkButton
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  setAgainModalProps({
                    name: topicGroupName,
                    onSubmit: () => {
                      r?.reopenSurvey();
                    },
                  });
                }}
                data-cy="send-again-button"
              >
                <StyledTooltip {...tooltipProps} title={t(r?.disabled ? 'todayWasAlreadySent' : 'sendAgainTooltip')}>
                  <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                </StyledTooltip>
              </LinkButton>
            );
          }
          const handleRowClick = () => {
            if (r) {
              history.push(r.detailUrl);
            }
          };
          return (
            <>
              <div className="row" onClick={handleRowClick}>
                <div className="col-4 d-flex flex-column align-items-start">
                  <GrayBodySmall>{topicGroupName}</GrayBodySmall>
                  {r?.unread && isDetail && (
                    <Label color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                      {t('unreadChannel')}
                    </Label>
                  )}
                </div>

                <div className="col-6 d-flex align-items-center">{infoNodes}</div>
                <div className="col-2 d-flex align-items-center">{sendNode}</div>
              </div>

              <StyledHr />
            </>
          );
        })}
        {showDeleteButton &&
          !(
            respondent.respondent.respondent.deleted ||
            respondent.respondent.group.deletedRespondents?.includes(respondent.respondent.respondent.id)
          ) &&
          !isHiring && (
            <StyledLinkButton
              onClick={(event) => {
                event.stopPropagation();
                showModal(respondent.respondent.respondent, respondent.respondent);
              }}
              className="d-flex align-items-center justify-content-center"
              disabled={respondent.isDeleteDisabled}
              data-cy="delete-respondent-btn"
            >
              <DeleteIcon />
              <ButtonText>
                {t(respondent.isDeleteDisabled ? 'deleteRespondentDisabled' : 'deleteRespondent')}
              </ButtonText>
            </StyledLinkButton>
          )}
      </StyledCard>

      <SendAgainModal
        show={!!sendAgainModalProps}
        hideModal={() => setAgainModalProps(null)}
        name={sendAgainModalProps?.name || ''}
        onSubmit={
          sendAgainModalProps?.onSubmit ||
          (() => {
            // do nothing
          })
        }
      />
      <RemoveRespondentModal
        show={modal}
        showModal={setModal}
        respondent={respondentToDelete}
        dateFrom={dateFrom}
        surveyGroupId={reportSequence.surveyGroup!.id}
        accessToken={accessToken}
        onReload={onReload}
        surveysWithAnswers={groupedRespondents
          .find((respondent) => respondent.respondents.find((resp) => resp.id === respondentToDelete?.id))
          ?.surveys?.filter((survey) =>
            survey.publicChannels.find(
              (channel) => channel.withAnswer && channel.respondentId === respondentToDelete?.id,
            ),
          )}
      />
    </>
  );
};

const StyledHr = styled.hr`
  background-color: ${theme.colors.borderMain.default};
  margin-top: ${theme.spacing.h};
  margin-bottom: ${theme.spacing.h};
`;

export default RespondentDetailFeelings;
